// ExecHrPage — 인사관리 임원 대시보드 (ported from design/exec-views.jsx HrOpsView)
import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";

// ── Types ────────────────────────────────────────────────────────────────────

type HrKpis = {
  headcount: number;
  open_positions: number;
  turnover_rate: number;
  avg_tenure_years: number;
};

type FunnelStage = { stage: string; count: number };

type OpenPosition = {
  id: number;
  req_code: string;
  title: string;
  emp_type: string;
  target_count: number;
  filled_count: number;
  opened_at: string;
};

type EvalItem = {
  id: number;
  employee_name: string;
  department: string;
  eval_type: string;
  due_date: string;
  status: string;
};

type PayrollSummary = {
  period: string;
  employee_count: number;
  total_gross: number;
  total_net: number;
};

type HrOpsData = {
  kpis: HrKpis;
  recruitment_funnel: FunnelStage[];
  open_positions: OpenPosition[];
  eval_queue: EvalItem[];
  payroll_summary: PayrollSummary;
};

// ── Fallback demo data (design/exec-views.jsx 기준) ──────────────────────────

const DEMO: HrOpsData = {
  kpis: { headcount: 87, open_positions: 5, turnover_rate: 3.2, avg_tenure_years: 3.4 },
  recruitment_funnel: [
    { stage: "지원", count: 24 },
    { stage: "서류", count: 12 },
    { stage: "1차", count: 6 },
    { stage: "최종", count: 3 },
    { stage: "오퍼", count: 2 },
  ],
  open_positions: [
    { id: 1, req_code: "JR-018", title: "시니어 프론트엔드 개발자", emp_type: "FULLTIME", target_count: 2, filled_count: 0, opened_at: "2026-04-15" },
    { id: 2, req_code: "JR-019", title: "데이터 엔지니어", emp_type: "FULLTIME", target_count: 1, filled_count: 0, opened_at: "2026-04-22" },
    { id: 3, req_code: "JR-020", title: "영업 매니저", emp_type: "FULLTIME", target_count: 1, filled_count: 0, opened_at: "2026-05-01" },
    { id: 4, req_code: "JR-021", title: "UX 디자이너", emp_type: "CONTRACT", target_count: 1, filled_count: 0, opened_at: "2026-05-08" },
  ],
  eval_queue: [
    { id: 1, employee_name: "김민준", department: "개발팀", eval_type: "정기평가", due_date: "2026-06-30", status: "PENDING" },
    { id: 2, employee_name: "박지수", department: "마케팅팀", eval_type: "수습평가", due_date: "2026-05-31", status: "IN_REVIEW" },
    { id: 3, employee_name: "이서연", department: "영업팀", eval_type: "목표설정", due_date: "2026-06-15", status: "PENDING" },
    { id: 4, employee_name: "최현우", department: "기획팀", eval_type: "정기평가", due_date: "2026-06-30", status: "PENDING" },
  ],
  payroll_summary: { period: "2026-05", employee_count: 87, total_gross: 456000000, total_net: 356000000 },
};

// ── Helper ────────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 100_000_000
    ? `${(n / 100_000_000).toFixed(1)}억`
    : n >= 10_000
    ? `${Math.round(n / 10000).toLocaleString()}만`
    : n.toLocaleString();

const EVAL_STATUS_COLOR: Record<string, string> = {
  PENDING: "#D97706",
  IN_REVIEW: "#2563EB",
  COMPLETED: "#16A34A",
};
const EVAL_STATUS_LABEL: Record<string, string> = {
  PENDING: "대기",
  IN_REVIEW: "검토 중",
  COMPLETED: "완료",
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ExecHrPage() {
  const [data, setData] = useState<HrOpsData | null>(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    api.get<HrOpsData>("/executive/hr-ops")
      .then((r) => setData(r.data))
      .catch(() => setData(DEMO))
      .finally(() => setLoading(false));
  }, []);

  const d = data ?? DEMO;
  const maxFunnel = Math.max(...d.recruitment_funnel.map((f) => f.count), 1);

  return (
    <div style={{ padding: 20, overflow: "auto", height: "100%" }}>
      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "총 인원", value: `${d.kpis.headcount}명`, icon: "Users", color: "#2563EB" },
          { label: "채용 중", value: `${d.kpis.open_positions}건`, icon: "Plus", color: "#D97706" },
          { label: "이직률(YTD)", value: `${d.kpis.turnover_rate}%`, icon: "TrendDown", color: "#EF4444" },
          { label: "평균 근속", value: `${d.kpis.avg_tenure_years}년`, icon: "Clock", color: "#16A34A" },
        ].map((k) => (
          <div key={k.label} className="t-card" style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: "var(--c-text-3)", fontWeight: 500 }}>{k.label}</span>
              <span style={{ width: 28, height: 28, borderRadius: 6, background: `${k.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <I.Users size={14} style={{ color: k.color }} />
              </span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "var(--c-text-1)" }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Recruitment funnel */}
        <div className="t-card" style={{ padding: "16px" }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14, color: "var(--c-text-1)" }}>채용 퍼널</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {d.recruitment_funnel.map((f) => (
              <div key={f.stage} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 32, fontSize: 11, color: "var(--c-text-3)", textAlign: "right" }}>{f.stage}</span>
                <div style={{ flex: 1, height: 16, background: "var(--c-surface-2)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 4, background: "#2563EB",
                    width: `${(f.count / maxFunnel) * 100}%`,
                    transition: "width .3s",
                    display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 4,
                  }}>
                    {f.count >= 3 && <span style={{ fontSize: 9, color: "#fff", fontWeight: 700 }}>{f.count}</span>}
                  </div>
                </div>
                <span style={{ width: 20, fontSize: 11, color: "var(--c-text-2)", fontWeight: 600 }}>{f.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payroll summary */}
        <div className="t-card" style={{ padding: "16px" }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14, color: "var(--c-text-1)" }}>
            급여 현황 <span style={{ fontWeight: 400, fontSize: 11, color: "var(--c-text-3)" }}>{d.payroll_summary.period}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "급여 대상", value: `${d.payroll_summary.employee_count}명` },
              { label: "총 지급 (세전)", value: fmt(d.payroll_summary.total_gross) },
              { label: "총 실지급 (세후)", value: fmt(d.payroll_summary.total_net), highlight: true },
            ].map((row) => (
              <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "var(--c-text-3)" }}>{row.label}</span>
                <span style={{ fontSize: 14, fontWeight: row.highlight ? 700 : 500, color: row.highlight ? "#2563EB" : "var(--c-text-1)" }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--c-border)" }}>
            <div style={{ fontSize: 11, color: "var(--c-text-3)" }}>공제율</div>
            <div style={{ marginTop: 4, height: 6, background: "var(--c-surface-2)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 3, background: "#16A34A",
                width: `${(d.payroll_summary.total_net / d.payroll_summary.total_gross) * 100}%`,
              }} />
            </div>
            <div style={{ fontSize: 10, color: "var(--c-text-3)", marginTop: 3 }}>
              실지급률 {d.payroll_summary.total_gross > 0 ? Math.round((d.payroll_summary.total_net / d.payroll_summary.total_gross) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Open positions */}
      <div className="t-card" style={{ padding: "16px", marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: "var(--c-text-1)" }}>채용 포지션</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
              {["코드", "포지션", "고용 형태", "목표/충원", "오픈일"].map((h) => (
                <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontSize: 10, color: "var(--c-text-3)", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.open_positions.map((pos) => (
              <tr key={pos.id} style={{ borderBottom: "1px solid var(--c-border-soft)" }}>
                <td style={{ padding: "8px 8px", color: "var(--c-text-3)", fontFamily: "monospace", fontSize: 11 }}>{pos.req_code}</td>
                <td style={{ padding: "8px 8px", fontWeight: 500, color: "var(--c-text-1)" }}>{pos.title}</td>
                <td style={{ padding: "8px 8px" }}>
                  <span style={{
                    fontSize: 10, padding: "2px 6px", borderRadius: 4,
                    background: pos.emp_type === "FULLTIME" ? "#2563EB15" : "#D9770615",
                    color: pos.emp_type === "FULLTIME" ? "#2563EB" : "#D97706",
                  }}>
                    {pos.emp_type === "FULLTIME" ? "정규직" : pos.emp_type === "CONTRACT" ? "계약직" : pos.emp_type}
                  </span>
                </td>
                <td style={{ padding: "8px 8px", color: "var(--c-text-2)" }}>
                  {pos.filled_count} / {pos.target_count}
                </td>
                <td style={{ padding: "8px 8px", color: "var(--c-text-3)", fontSize: 11 }}>{pos.opened_at}</td>
              </tr>
            ))}
            {d.open_positions.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 20, color: "var(--c-text-3)" }}>진행 중인 채용 포지션 없음</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Evaluation queue */}
      <div className="t-card" style={{ padding: "16px" }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: "var(--c-text-1)" }}>평가 대기</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {d.eval_queue.map((e) => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", background: "var(--c-surface-2)", borderRadius: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: "var(--c-text-1)" }}>{e.employee_name}</div>
                <div style={{ fontSize: 11, color: "var(--c-text-3)", marginTop: 2 }}>{e.department} · {e.eval_type}</div>
              </div>
              <div style={{ fontSize: 11, color: "var(--c-text-3)" }}>마감 {e.due_date}</div>
              <span style={{
                fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 600,
                background: `${EVAL_STATUS_COLOR[e.status] ?? "#94A3B8"}15`,
                color: EVAL_STATUS_COLOR[e.status] ?? "#94A3B8",
              }}>
                {EVAL_STATUS_LABEL[e.status] ?? e.status}
              </span>
            </div>
          ))}
          {d.eval_queue.length === 0 && (
            <div style={{ textAlign: "center", padding: 20, color: "var(--c-text-3)", fontSize: 12 }}>대기 중인 평가 없음</div>
          )}
        </div>
      </div>
    </div>
  );
}
